import React from "react";
import PropTypes from "prop-types";
import {
    FlaskConical,
    LayoutDashboard,
    Plus,
    List,
    Lightbulb,
    LineChart,
    SlidersHorizontal,
    ChevronRight,
    Cpu,
    Pause,
    Play,
} from "lucide-react";
import { labHref, navigate } from "./labRouter";
import { useLabHealth } from "./LabAccessGate";

const NAV_ITEMS = [
    { key: "dashboard",      label: "Dashboard",       href: labHref(""),                 icon: LayoutDashboard },
    { key: "submit",         label: "New experiment",  href: labHref("submit"),           icon: Plus },
    { key: "experiments",    label: "Experiments",     href: labHref("experiments"),      icon: List },
    { key: "recommendations",label: "Recommendations", href: labHref("recommendations"),  icon: Lightbulb },
    { key: "history",        label: "History",         href: labHref("history"),          icon: LineChart },
    { key: "settings",       label: "Controls",        href: labHref("settings"),         icon: SlidersHorizontal },
];

const NavLink = ({ item, active }) => {
    const Icon = item.icon;
    return (
        <a
            href={item.href}
            onClick={(e) => {
                e.preventDefault();
                navigate(item.href);
            }}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                active
                    ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
        >
            <Icon className="w-4 h-4" />
            {item.label}
        </a>
    );
};

const PipelineBadge = ({ health }) => {
    if (!health) return null;
    const endpoint = health.endpoint;
    const pipeline = health.pipeline;
    const paused = pipeline?.paused;
    const endpointOk = endpoint?.status === "InService";
    return (
        <div className="px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 mb-2">
                <Cpu className="w-3 h-3" /> Pipeline
            </div>
            <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Endpoint</span>
                <span className={`font-mono ${endpointOk ? "text-emerald-600" : "text-slate-400"}`}>
                    {endpoint?.status || "—"}
                </span>
            </div>
            <div className="flex items-center justify-between text-[11px] mt-1">
                <span className="text-slate-500">Pending</span>
                <span className="font-mono text-slate-700 dark:text-slate-200">
                    {pipeline?.inbox_pending_count ?? "—"}
                </span>
            </div>
            <div className="flex items-center justify-between text-[11px] mt-1">
                <span className="text-slate-500">Running</span>
                <span className="font-mono text-slate-700 dark:text-slate-200">
                    {pipeline?.currently_running_executions ?? "—"}
                </span>
            </div>
            <div className="mt-2 flex items-center gap-1 text-[10px]">
                {paused ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                        <Pause className="w-2.5 h-2.5" /> paused
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                        <Play className="w-2.5 h-2.5" /> live
                    </span>
                )}
            </div>
        </div>
    );
};

const breadcrumbFor = (activeKey, extra) => {
    const label = NAV_ITEMS.find((i) => i.key === activeKey)?.label || "Lab";
    return (
        <div className="flex items-center gap-1.5 text-sm text-slate-500">
            <a
                href={labHref("")}
                onClick={(e) => { e.preventDefault(); navigate(labHref("")); }}
                className="hover:text-slate-700 dark:hover:text-slate-200"
            >
                Lab
            </a>
            {label !== "Dashboard" ? (
                <>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="text-slate-700 dark:text-slate-200">{label}</span>
                </>
            ) : null}
            {extra ? (
                <>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="text-slate-700 dark:text-slate-200 font-mono text-xs">{extra}</span>
                </>
            ) : null}
        </div>
    );
};

const LabShell = ({ activeKey, breadcrumbExtra, headerAction, children }) => {
    const health = useLabHealth();
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
            <div className="flex">
                <aside className="hidden md:flex md:flex-col w-60 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 min-h-screen p-4 gap-1">
                    <a
                        href={labHref("")}
                        onClick={(e) => { e.preventDefault(); navigate(labHref("")); }}
                        className="flex items-center gap-2 px-2 py-3 mb-2"
                    >
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                            <FlaskConical className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold leading-tight">The Lab</div>
                            <div className="text-[11px] text-slate-500 leading-tight">Ultra · Alpamayo 1.5</div>
                        </div>
                    </a>
                    {NAV_ITEMS.map((item) => (
                        <NavLink key={item.key} item={item} active={item.key === activeKey} />
                    ))}
                    <div className="mt-auto pt-4">
                        <PipelineBadge health={health} />
                    </div>
                </aside>

                <main className="flex-1 min-w-0 flex flex-col">
                    <header className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-6 py-3">
                        <div className="flex items-center justify-between gap-4">
                            {breadcrumbFor(activeKey, breadcrumbExtra)}
                            <div className="flex items-center gap-2">{headerAction}</div>
                        </div>
                    </header>
                    <div className="flex-1 px-6 py-6 max-w-6xl w-full mx-auto">
                        {children}
                    </div>
                </main>
            </div>

            <div className="md:hidden fixed inset-x-0 bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-2 py-2 z-20">
                <div className="flex justify-between gap-1 overflow-x-auto">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.key}
                            href={item.href}
                            onClick={(e) => { e.preventDefault(); navigate(item.href); }}
                            className={`flex flex-col items-center px-2 py-1.5 rounded-md text-[10px] flex-shrink-0 ${
                                item.key === activeKey
                                    ? "text-indigo-600 dark:text-indigo-400"
                                    : "text-slate-500"
                            }`}
                        >
                            <item.icon className="w-4 h-4 mb-0.5" />
                            {item.label}
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
};

LabShell.propTypes = {
    activeKey: PropTypes.string.isRequired,
    breadcrumbExtra: PropTypes.node,
    headerAction: PropTypes.node,
    children: PropTypes.node,
};

export default LabShell;
